import { Button as AntButton, message } from 'antd';
import { DropdownCopy } from 'components';
import { format, isValid, parseISO } from 'date-fns';
import { UseFormReturn, Controller, useWatch } from 'react-hook-form';
import { Days } from 'shared/constants/DAYS';
import { DailySchedule, IProgramSetupForm } from 'shared/interfaces/IProgramSetup';
import { TimePicker } from 'shared/theme/elements';
import { useMemo, useState } from 'react';
import { AutomationToggle } from './components/AutomationToggle';
import { PsToasts, usePsToast } from './components/Toast';
import {
  Card,
  CardBody,
  CardHead,
  CardHeadAside,
  CardIc,
  CardSub,
  CardTitle,
  CardTitles,
  Day,
  DayModeBtn,
  Pulse,
  ScheduleFoot,
  Tbl,
  ToggleLabel,
  ToggleRow,
  ToggleSub,
  TRow,
} from './ProgramSetup.styled';

interface Props {
  form: UseFormReturn<IProgramSetupForm, object>;
  isLoading?: boolean;
  currentChannelId?: string;
  existingPrograms?: Array<{
    _id: string;
    channel_details: { _id: string };
    daily_schedule: DailySchedule;
  }>;
  currentProgramId?: string; // To exclude current program from validation
}

const days = Object.entries(Days);
const daysArrObj = Object.entries(Days).map(([key, value]) => ({ id: value, name: key }));
const DEFAULT_SLOT = '1990-01-01 00:00:00';

const isDefaultTime = (str: string | null | undefined) =>
  !str || str === DEFAULT_SLOT || str === '00:00' || str === '12:00 AM';

// Helper function to check if two time ranges overlap
const doTimeslotsOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
  if (isDefaultTime(start1) || isDefaultTime(end1) || isDefaultTime(start2) || isDefaultTime(end2)) {
    return false;
  }

  const start1Time = new Date(start1).getTime();
  const end1Time = new Date(end1).getTime();
  const start2Time = new Date(start2).getTime();
  const end2Time = new Date(end2).getTime();

  return start1Time < end2Time && start2Time < end1Time;
};

// Helper function to check for overlaps with other programs in the same channel
const checkChannelOverlap = (
  day: string,
  newStart: string | undefined,
  newEnd: string | undefined,
  currentChannelId: string | undefined,
  existingPrograms: Array<{ _id: string; channel_details: { _id: string }; daily_schedule: DailySchedule }> | undefined,
  currentProgramId: string | undefined,
): boolean => {
  if (!currentChannelId || !existingPrograms || !newStart || !newEnd) {
    return false;
  }

  // Filter programs in the same channel, excluding the current program
  const sameChannelPrograms = existingPrograms.filter(
    (program) => program.channel_details._id === currentChannelId && program._id !== currentProgramId,
  );

  // Check for overlaps with each program in the same channel
  for (const program of sameChannelPrograms) {
    const existingSchedule = program.daily_schedule[day as keyof DailySchedule];
    if (existingSchedule && existingSchedule.timeslot_start && existingSchedule.timeslot_end) {
      if (doTimeslotsOverlap(newStart, newEnd, existingSchedule.timeslot_start, existingSchedule.timeslot_end)) {
        return true;
      }
    }
  }

  return false;
};

export const ProgramSetupTimeslot = ({
  form,
  isLoading,
  currentChannelId,
  existingPrograms,
  currentProgramId,
}: Props) => {
  const { getValues, setValue, control } = form;
  const { toasts, push: toast } = usePsToast();

  const [lastTimes, setLastTimes] = useState<Record<string, { start: string; end: string }>>({});

  const dailySchedule = useWatch({ control, name: 'daily_schedule' }) || getValues().daily_schedule;
  const isRowOff = (key: string) =>
    isDefaultTime(dailySchedule?.[key as keyof DailySchedule]?.timeslot_start) &&
    isDefaultTime(dailySchedule?.[key as keyof DailySchedule]?.timeslot_end);
  const isRowAuto = (key: string) => !!dailySchedule?.[key as keyof DailySchedule]?.automation_enabled;
  const masterAuto = useMemo(() => days.some(([, key]) => isRowAuto(key)), [dailySchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRowOff = (key: keyof DailySchedule, off: boolean) => {
    if (off) {
      const current = getValues().daily_schedule?.[key];
      if (current && !isDefaultTime(current.timeslot_start) && !isDefaultTime(current.timeslot_end)) {
        setLastTimes((c) => ({
          ...c,
          [key]: { start: current.timeslot_start as string, end: current.timeslot_end as string },
        }));
      }
      setValue(`daily_schedule.${key}.timeslot_start`, DEFAULT_SLOT);
      setValue(`daily_schedule.${key}.timeslot_end`, DEFAULT_SLOT);
      setValue(`daily_schedule.${key}.automation_enabled`, false);
    } else {
      const prev = lastTimes[key];
      // A day with no prior time can't fall back to '' here — isDefaultTime()
      // treats '' as still off, which would leave the row (and its now-enabled
      // TimePicker/Automation toggle) stuck showing as Off forever.
      setValue(`daily_schedule.${key}.timeslot_start`, prev?.start || '1990-01-01 09:00:00');
      setValue(`daily_schedule.${key}.timeslot_end`, prev?.end || '1990-01-01 17:00:00');
    }
  };

  const toggleMaster = () => {
    if (!masterAuto) {
      const eligible = days.filter(([, key]) => !isRowOff(key));
      if (!eligible.length) {
        toast('No day is on schedule yet — set a Time Start first', 'bad');
        return;
      }
      eligible.forEach(([, key]) => setValue(`daily_schedule.${key}.automation_enabled`, true));
      const skipped = days.filter(([, key]) => isRowOff(key)).map(([day]) => day);
      toast(
        `Automation enabled for ${eligible.length} day${eligible.length === 1 ? '' : 's'}` +
          (skipped.length ? ` · ${skipped.join(', ')} left off (not on schedule)` : ''),
        'ok',
      );
    } else {
      days.forEach(([, key]) => setValue(`daily_schedule.${key}.automation_enabled`, false));
      toast('Automation turned off for all days');
    }
  };

  const handleDailyScheduleCopy = (origin?: string, destination?: string[]) => {
    const originValue = {
      timeslot_start: origin ? getValues().daily_schedule?.[origin].timeslot_start : origin,
      timeslot_end: origin ? getValues().daily_schedule?.[origin].timeslot_end : origin,
    };

    destination?.forEach((day) => {
      setValue(`daily_schedule.${day as keyof DailySchedule}`, originValue);
    });
  };

  return (
    <Card id="secSchedule">
      <CardHead>
        <CardIc $tone="time">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3.5 2" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Time Slot &amp; Automation</CardTitle>
          <CardSub>Leave default if no program for the day</CardSub>
        </CardTitles>
        <CardHeadAside>
          <ToggleRow>
            <AutomationToggle on={masterAuto} onChange={toggleMaster} disabled={isLoading} label="Enable Automation" />
            <div>
              <ToggleLabel>Enable Automation</ToggleLabel>
              <ToggleSub>Automatically record using this schedule</ToggleSub>
            </div>
          </ToggleRow>
        </CardHeadAside>
      </CardHead>
      <CardBody>
        <Tbl>
          <TRow $thead>
            <div>DAY</div>
            <div>TIME START</div>
            <div>TIME END</div>
            <div>AUTOMATION</div>
            <div />
          </TRow>
          {days.map(([day, key]) => {
            const off = isRowOff(key);
            return (
              <TRow key={key} $off={off}>
                <Day>{day}</Day>
                <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
                  <DayModeBtn
                    type="button"
                    $off={off}
                    disabled={isLoading}
                    aria-label={`${day} on or off schedule`}
                    onClick={() => setRowOff(key, !off)}
                  >
                    {off ? 'Off' : 'On'}
                  </DayModeBtn>
                  <Controller
                    control={control}
                    name={`daily_schedule.${key}.timeslot_start`}
                    render={({ field }) => (
                      <TimePicker
                        format="h:mm a"
                        size="middle"
                        showNow={false}
                        value={
                          isValid(parseISO(field.value || ''))
                            ? parseISO(field.value || '')
                            : new Date('1990-01-01 00:00:00')
                        }
                        onChange={(newValue) => {
                          const newStart = newValue ? format(new Date(newValue), '1990-01-01 HH:mm:ss') : undefined;
                          const currentEnd = getValues().daily_schedule?.[key]?.timeslot_end;

                          const isSettingDefaultTimeslot =
                            newStart && currentEnd && newStart.includes('00:00:00') && currentEnd.includes('00:00:00');
                          if (isSettingDefaultTimeslot) {
                            field.onChange(newStart);
                            return;
                          }
                          if (newStart && currentEnd && !isDefaultTime(currentEnd)) {
                            if (
                              checkChannelOverlap(
                                key,
                                newStart,
                                currentEnd,
                                currentChannelId,
                                existingPrograms,
                                currentProgramId,
                              )
                            ) {
                              message.error('This timeslot overlaps with another program in the same channel!');
                              return;
                            }
                          }
                          field.onChange(newStart);
                        }}
                        disabled={isLoading || off}
                        $fullWidth
                      />
                    )}
                  />
                </div>
                <Controller
                  control={control}
                  name={`daily_schedule.${key}.timeslot_end`}
                  render={({ field }) => (
                    <TimePicker
                      format="h:mm a"
                      size="middle"
                      showNow={false}
                      value={
                        isValid(parseISO(field.value || ''))
                          ? parseISO(field.value || '')
                          : new Date('1990-01-01 00:00:00')
                      }
                      onChange={(newValue) => {
                        const newEnd = newValue ? format(new Date(newValue), '1990-01-01 HH:mm:ss') : undefined;
                        const currentStart = getValues().daily_schedule?.[key]?.timeslot_start;

                        const isSettingDefaultTimeslot =
                          currentStart && newEnd && currentStart.includes('00:00:00') && newEnd.includes('00:00:00');
                        if (isSettingDefaultTimeslot) {
                          field.onChange(newEnd);
                          return;
                        }
                        if (currentStart && newEnd && !isDefaultTime(currentStart)) {
                          if (
                            checkChannelOverlap(
                              key,
                              currentStart,
                              newEnd,
                              currentChannelId,
                              existingPrograms,
                              currentProgramId,
                            )
                          ) {
                            message.error('This timeslot overlaps with another program in the same channel!');
                            return;
                          }
                        }
                        field.onChange(newEnd);
                      }}
                      disabled={isLoading || off}
                      $fullWidth
                    />
                  )}
                />
                <AutomationToggle
                  on={!off && isRowAuto(key)}
                  onChange={(on) => setValue(`daily_schedule.${key}.automation_enabled`, on)}
                  disabled={isLoading || off}
                  label={`Enable Automation for ${day}`}
                />
                <DropdownCopy items={daysArrObj} current={key} onCopy={handleDailyScheduleCopy} disabled={isLoading}>
                  <AntButton disabled={isLoading}>Copy</AntButton>
                </DropdownCopy>
              </TRow>
            );
          })}
        </Tbl>

        <ScheduleFoot>
          <span>
            <Pulse $off={!masterAuto} /> <b>{masterAuto ? 'Automation enabled' : 'Automation disabled'}</b>
          </span>
        </ScheduleFoot>
      </CardBody>
      <PsToasts toasts={toasts} />
    </Card>
  );
};
